import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getSettings } from "../services/settings";
import mcbLogo from "../images/mcb-logo.png";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4040/api";
const BOOKINGS_API = `${API_BASE}/bookings`;
const SAILINGS_API = `${API_BASE}/sailings`;
const INSPECTIONS_API = `${API_BASE}/inspections`;
const INCIDENTS_API = `${API_BASE}/incidents`;

// Shared input class
const INPUT_SM = "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-950 focus:ring-2 focus:ring-slate-950/10";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "loading", label: "Loading" },
  { key: "compliance", label: "Compliance" },
  { key: "inspections", label: "Inspections" },
  { key: "incidents", label: "Incidents" },
  { key: "reports", label: "Reports" },
];

const INCIDENT_TYPES = ["Welfare Concern", "Injury", "Mortality", "Heat Stress", "Escape / Near Miss", "Aggressive Animal", "Pen Failure", "Other"];

export default function Livestock() {
  const [activeTab, setActiveTab] = useState("overview");
  const [bookings, setBookings] = useState([]);
  const [sailings, setSailings] = useState([]);
  const [loading, setLoading] = useState(true);

  // Incidents
  const [incidents, setIncidents] = useState([]);
  const [showIncidentForm, setShowIncidentForm] = useState(false);
  const [incident, setIncident] = useState({ type: "", sailingRef: "", penNumber: "", tagId: "", description: "", outcome: "", date: new Date().toISOString().slice(0, 10) });

  // Inspections
  const [inspections, setInspections] = useState([]);
  const [showInspectionForm, setShowInspectionForm] = useState(false);
  const [expandedInspectionIds, setExpandedInspectionIds] = useState(() => new Set());
  const [inspection, setInspection] = useState({
    sailingRef: "", penNumber: "", date: new Date().toISOString().slice(0, 10), time: "",
    animalsStanding: "", lameness: "", heatStress: "", waterOk: "", fodderOk: "",
    deckDry: "", ventilation: "", ammonia: "", gatesOk: "", notes: "",
  });

  // Scanning
  const [scanFeedback, setScanFeedback] = useState(null); // { type: 'ok'|'duplicate'|'unmatched', eid, message, penNumber }
  const [scanTargets, setScanTargets] = useState({}); // { [sailingId]: { penNumber, bookingId, customerName } }
  const [scanningEnabled, setScanningEnabled] = useState(false);

  // Reports
  const [reportSailingId, setReportSailingId] = useState("");
  const [reportType, setReportType] = useState("");

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [bRes, sRes, iRes, incRes] = await Promise.all([
        fetch(BOOKINGS_API),
        fetch(SAILINGS_API),
        fetch(INSPECTIONS_API),
        fetch(INCIDENTS_API),
      ]);
      const bData = await bRes.json();
      const sData = await sRes.json();
      const iData = await iRes.json();
      const incData = await incRes.json();
      console.log("bookings:", bData);
      console.log("sailings:", sData);
      setBookings(Array.isArray(bData) ? bData : (bData?.data || []));
      setSailings(Array.isArray(sData) ? sData : (sData?.data || []));
      setInspections(Array.isArray(iData) ? iData : (iData?.data || []));
      setIncidents(Array.isArray(incData) ? incData : (incData?.data || []));

      try {
        const settings = await getSettings();
        setScanningEnabled(Boolean(settings?.booking?.naitScanningEnabled));
      } catch (settingsErr) {
        console.error("Livestock settings fetch error:", settingsErr);
        setScanningEnabled(false);
      }
    } catch (err) {
      console.error("Livestock loadData error:", err);
    }
    finally { setLoading(false); }
  };

  const generateReport = (type, sailingId) => {
    const sailing = sailings.find(s => s._id === sailingId);
    const sailingBookings = bookings.filter(b => String(b.sailing) === sailingId || String(b.sailing?._id) === sailingId);
    const livestockBks = sailingBookings.filter(b => b.bookingType === "livestock" || b.bookingType === "mixed");
    const sailingIncidents = incidents.filter(i => i.sailingRef === sailing?.sailingRef);
    const sailingInspections = inspections.filter(i => i.sailingRef === sailing?.sailingRef);
    const now = new Date().toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" });

    const styles = `body{font-family:Arial,Helvetica,sans-serif;font-size:10px;color:#1a1a1a;margin:0;padding:20px 24px}h1{font-size:16px;font-weight:900;color:#7a1428;margin:0 0 2px}h2{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:#7a1428;border-bottom:1px solid #eee;padding-bottom:4px;margin:16px 0 8px}.hdr{display:flex;justify-content:space-between;border-bottom:3px solid #7a1428;padding-bottom:12px;margin-bottom:14px}.meta{font-size:9px;color:#888}table{width:100%;border-collapse:collapse;margin-bottom:14px;font-size:9px}th{background:#1a1a1a;color:#fff;padding:5px 8px;text-align:left;font-size:8px;text-transform:uppercase}td{padding:5px 8px;border-bottom:1px solid #eee}tr:nth-child(even) td{background:#fafafa}.ok{color:#007a3d;font-weight:700}.fail{color:#c00;font-weight:700}.footer{margin-top:16px;padding-top:8px;border-top:1px solid #eee;display:flex;justify-content:space-between;font-size:8px;color:#bbb}@page{size:A4;margin:12mm}.insp-table th{padding:8px 10px;font-size:9px}.insp-table td{padding:9px 10px;font-size:10px;vertical-align:top;line-height:1.5}`;

    const voyageMeta = sailing ? `<div style="display:grid;grid-template-columns:repeat(6,1fr);border:1px solid #ddd;margin-bottom:14px;border-radius:3px;overflow:hidden">${[["Sailing Ref",sailing.sailingRef],["Vessel",sailing.vesselName||"William Fraser"],["Route",`${sailing.origin} → ${sailing.destination}`],["Status",sailing.status],["ETD",sailing.etd?new Date(sailing.etd).toLocaleDateString("en-NZ"):"—"],["Bookings",sailingBookings.length]].map(([l,v],i)=>`<div style="padding:7px 10px;border-right:${i<5?"1px solid #ddd":"none"};background:${i%2===0?"#fafafa":"#fff"}"><div style="font-size:8px;text-transform:uppercase;color:#999;margin-bottom:2px">${l}</div><div style="font-size:11px;font-weight:600">${v||"—"}</div></div>`).join("")}</div>` : "";

    const hdr = `<div class="hdr"><div><img src="${mcbLogo}" alt="McCallum Bros Ltd" style="height:44px;width:auto;" /><div class="meta" style="margin-top:6px;">Established 1904 · 747 Rosebank Road, Avondale, Auckland</div></div><div style="text-align:right"><div style="font-size:14px;font-weight:800">${type.toUpperCase()}</div><div class="meta">Generated: ${now}</div></div></div>${voyageMeta}`;

    let body = "";
    const totalHead = livestockBks.reduce((s,b)=>s+(b.livestockItems||[]).reduce((ss,i)=>ss+Number(i.headCount||0),0),0);
    const fitCount = livestockBks.filter(b=>b.livestockItems?.[0]?.fitForTransport).length;

    if (type === "Load Report") {
      const pens = sailing?.loadPlan?.pens || [];
      body = `<h2>Pen Allocation</h2><table><tr><th>Pen</th><th>Side</th><th>Area m²</th><th>Customer</th><th>Class</th><th>Head</th><th>Avg Wt</th><th>Cap %</th></tr>${pens.map(pen=>pen.allocations?.length?pen.allocations.map(a=>`<tr><td><b>${pen.penNumber}</b></td><td>${pen.penNumber?.startsWith("P")?"Port":"Starboard"}</td><td>${pen.areaM2||"—"}</td><td>${a.customerName}</td><td>${a.animalClass||"—"}</td><td><b>${a.headCount}</b></td><td>${a.avgWeightKg?a.avgWeightKg+" kg":"—"}</td><td>${pen.capacityPct||0}%</td></tr>`).join(""):`<tr><td><b>${pen.penNumber}</b></td><td colspan="7" style="color:#ccc;font-style:italic">Empty${pen.isHospitalPen?" — Hospital":""}</td></tr>`).join("")}</table><h2>Consignment Detail</h2><table><tr><th>Ref</th><th>Customer</th><th>Species</th><th>Class</th><th>Head</th><th>Avg Wt (kg)</th><th>Last Fed</th><th>Last Watered</th><th>Fit for Transport</th></tr>${livestockBks.map(b=>(b.livestockItems||[]).map(lv=>`<tr><td>${b.bookingRef}</td><td>${b.customerName}</td><td>${lv.livestockType||"—"}</td><td>${lv.animalClass||"—"}</td><td><b>${lv.headCount||0}</b></td><td>${lv.averageWeightKg||"—"}</td><td>${lv.lastFed?new Date(lv.lastFed).toLocaleString("en-NZ",{dateStyle:"short",timeStyle:"short"}):"—"}</td><td>${lv.lastWatered?new Date(lv.lastWatered).toLocaleString("en-NZ",{dateStyle:"short",timeStyle:"short"}):"—"}</td><td class="${lv.fitForTransport?"ok":"fail"}">${lv.fitForTransport?"✓":"✗"}</td></tr>`).join("")).join("")}</table>`;
    }

    if (type === "Welfare & Inspection Report") {
      body = `<h2>Inspection Log</h2>${sailingInspections.length===0?"<p style='color:#999'>No inspections recorded.</p>":`<table class="insp-table"><colgroup><col style="width:9%"><col style="width:7%"><col style="width:14%"><col style="width:7%"><col style="width:9%"><col style="width:7%"><col style="width:7%"><col style="width:9%"><col style="width:31%"></colgroup><tr><th>Date</th><th>Time</th><th>Route</th><th>Pen</th><th>Animals OK</th><th>Water</th><th>Fodder</th><th>Ammonia</th><th>Notes</th></tr>${sailingInspections.map(ins=>{const s=sailings.find(sl=>sl.sailingRef===ins.sailingRef);return`<tr><td>${ins.date}</td><td>${ins.time||"—"}</td><td>${s?`${s.origin} → ${s.destination}`:"—"}</td><td>${ins.penNumber||"All"}</td><td class="${ins.animalsStanding==="ok"?"ok":"fail"}">${ins.animalsStanding==="ok"?"✓":"!"}</td><td class="${ins.waterOk==="ok"?"ok":"fail"}">${ins.waterOk==="ok"?"✓":"!"}</td><td class="${ins.fodderOk==="ok"?"ok":"fail"}">${ins.fodderOk==="ok"?"✓":"!"}</td><td style="color:${Number(ins.ammonia||0)>=25?"#c00":"inherit"}">${ins.ammonia||"—"} ppm</td><td>${ins.notes||"—"}</td></tr>`;}).join("")}</table>`}<h2>Compliance Checklist</h2><table><tr><th>Requirement</th><th>Reference</th><th>Status</th></tr>${[["Farmer Declaration received","Pre-Loading Step 1",sailing?.complianceChecklist?.farmerDeclarationReceived],["Load Plan approved","MNZ Rule Part 24c",sailing?.complianceChecklist?.loadPlanApproved||sailing?.loadPlan?.status==="approved"],["Hospital pen reserved","MNZ Rule Part 24c",sailing?.complianceChecklist?.hospitalPenReserved],["Prestart meeting completed","Loading Step 1",sailing?.complianceChecklist?.prestartMeetingCompleted]].map(([l,r,ok])=>`<tr><td>${l}</td><td style="color:#888;font-size:9px">${r}</td><td class="${ok?"ok":"fail"}">${ok?"✓":"✗"}</td></tr>`).join("")}</table>`;
    }

    if (type === "Incident Report") {
      body = `<h2>Welfare Incident Register</h2>${sailingIncidents.length===0?"<p style='color:#999'>No incidents recorded.</p>":`<table><tr><th>Date</th><th>Type</th><th>Pen</th><th>Tag ID</th><th>Description</th><th>Outcome</th></tr>${sailingIncidents.map(inc=>`<tr><td>${inc.date}</td><td style="font-weight:700;color:${inc.type==="Mortality"?"#c00":inc.type==="Injury"?"#c07700":"#1a1a1a"}">${inc.type}</td><td>${inc.penNumber||"—"}</td><td>${inc.tagId||"—"}</td><td>${inc.description}</td><td>${inc.outcome||"—"}</td></tr>`).join("")}</table>`}<div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:16px;border:1px solid #ddd;border-radius:4px;padding:14px;margin-top:8px">${["Master / Skipper","Stockman","Date & Time"].map(l=>`<div><div style="font-size:8px;text-transform:uppercase;color:#999;margin-bottom:8px">${l}</div><div style="border-bottom:1px solid #1a1a1a;height:32px;margin-bottom:4px"></div></div>`).join("")}</div>`;
    }

    if (type === "MPI Audit Report") {
      body = `<div style="display:grid;grid-template-columns:repeat(3,1fr);gap:0;border:1px solid #ddd;border-radius:3px;overflow:hidden;margin-bottom:14px">${[["Total Head",totalHead],["Bookings",livestockBks.length],["Fit for Transport",`${fitCount}/${livestockBks.length}`]].map(([l,v],i)=>`<div style="padding:10px 12px;border-right:${i<2?"1px solid #ddd":"none"};background:${i%2===0?"#fafafa":"#fff"}"><div style="font-size:8px;text-transform:uppercase;color:#999;margin-bottom:2px">${l}</div><div style="font-size:14px;font-weight:700">${v}</div></div>`).join("")}</div><h2>Compliance Against MNZ Rule Part 24c</h2><table><tr><th>Requirement</th><th>Reference</th><th>Status</th></tr>${[["Livestock Booking & Declaration Forms received","Step 1 — Pre-Loading",sailing?.complianceChecklist?.farmerDeclarationReceived],["Load plan per Table 4 density rules","Step 2 — Loading Plan",sailing?.complianceChecklist?.loadPlanApproved||sailing?.loadPlan?.status==="approved"],["Hospital pen reserved (1% of pen space)","MNZ Rule Part 24c",sailing?.complianceChecklist?.hospitalPenReserved],["Prestart meeting conducted","Loading Step 1",sailing?.complianceChecklist?.prestartMeetingCompleted],["Fit for transport declared","Animal Welfare Regulations",fitCount===livestockBks.length]].map(([l,r,ok])=>`<tr><td>${l}</td><td style="color:#888;font-size:9px">${r}</td><td class="${ok?"ok":"fail"}">${ok?"✓":"✗"}</td></tr>`).join("")}</table><h2>Incident Summary</h2><table><tr><th>Type</th><th>Count</th></tr>${["Mortality","Injury","Heat Stress","Welfare Concern","Other"].map(t=>`<tr><td>${t}</td><td>${sailingIncidents.filter(i=>i.type===t).length}</td></tr>`).join("")}</table>`;
    }

    if (type === "Animal Movement Report") {
      body = `<h2>Animal Movement Record (NAIT)</h2><p style="font-size:9px;color:#666;margin-bottom:10px">Required under the National Animal Identification and Tracing Act 2012. Retain for minimum 7 years.</p><table><tr><th>Booking Ref</th><th>Owner / Farmer</th><th>Species</th><th>Class</th><th>Head Count</th><th>Avg Weight (kg)</th><th>Origin</th><th>Destination</th><th>Movement Date</th></tr>${livestockBks.map(b=>(b.livestockItems||[]).map(lv=>`<tr><td>${b.bookingRef}</td><td>${b.customerName}</td><td>${lv.livestockType||"cattle"}</td><td>${lv.animalClass||"—"}</td><td><b>${lv.headCount||0}</b></td><td>${lv.averageWeightKg||"—"}</td><td>${sailing?.origin||"—"}</td><td>${sailing?.destination||"—"}</td><td>${sailing?.etd?new Date(sailing.etd).toLocaleDateString("en-NZ"):"—"}</td></tr>`).join("")).join("")}</table>`;
    }

    if (type === "Livestock Manifest") {
      body = `<h2>Livestock Manifest — MPI Submission</h2><table><tr><th>Ref</th><th>Customer</th><th>Species</th><th>Class</th><th>Head</th><th>Avg Wt (kg)</th><th>Total Wt (kg)</th><th>Fit for Transport</th><th>Pen(s)</th></tr>${livestockBks.map(b=>(b.livestockItems||[]).map(lv=>{const pens=(sailing?.loadPlan?.pens||[]).filter(p=>p.allocations?.some(a=>String(a.bookingId)===String(b._id)));return`<tr><td>${b.bookingRef}</td><td>${b.customerName}</td><td>${lv.livestockType||"cattle"}</td><td>${lv.animalClass||"—"}</td><td><b>${lv.headCount||0}</b></td><td>${lv.averageWeightKg||"—"}</td><td>${((lv.headCount||0)*(lv.averageWeightKg||0)).toLocaleString()}</td><td class="${lv.fitForTransport?"ok":"fail"}">${lv.fitForTransport?"✓":"✗"}</td><td>${pens.map(p=>p.penNumber).join(", ")||"—"}</td></tr>`;}).join("")).join("")}<tr style="background:#1a1a1a;color:#fff;font-weight:700"><td colspan="4">TOTAL</td><td>${totalHead}</td><td colspan="4"></td></tr></table>`;
    }

    const footer = `<div class="footer"><span>McCallum Bros Ltd · ${type} · ${sailing?.sailingRef||"All Voyages"}</span><span>Generated ${now} · Livestock Handling Procedures v1</span></div>`;
    const win = window.open("","_blank");
    win.document.write(`<!DOCTYPE html><html><head><title>${type}</title><style>${styles}</style></head><body>${hdr}${body}${footer}<script>window.onload=()=>window.print();</script></body></html>`);
    win.document.close();
  };

  const livestockBookings = useMemo(() =>
    bookings.filter(b => b.bookingType === "livestock" || b.bookingType === "mixed"), [bookings]);

  const now = new Date();
  const thisMonth = livestockBookings.filter(b => {
    const d = new Date(b.createdAt);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });

  const headThisMonth = thisMonth.reduce((sum, b) =>
    sum + (b.livestockItems?.reduce((s, i) => s + Number(i.headCount || 0), 0) || 0), 0);

  const headYTD = livestockBookings.filter(b =>
    new Date(b.createdAt).getFullYear() === now.getFullYear()
  ).reduce((sum, b) =>
    sum + (b.livestockItems?.reduce((s, i) => s + Number(i.headCount || 0), 0) || 0), 0);

  // A booking only counts as "fit for transport" once every livestock item on
  // it has been declared fit — one bad item shouldn't be hidden by another
  // item on the same booking that was declared fine (previously used .some(),
  // which let a single fit item clear the whole booking).
  const fitForTransportCount = livestockBookings.filter(b => (b.livestockItems?.length || 0) > 0 && b.livestockItems.every(i => i.fitForTransport)).length;
  // vetCertCount removed — Vet Certificate compliance tracking turned off for now
  // NOTE: this is a Fit-for-Transport declaration rate, not overall MPI
  // compliance — see "Fit for Transport Rate" label below. The Voyage
  // Compliance Status table (Farmer Decl / Load Plan / Hospital Pen /
  // Prestart) is the actual per-sailing compliance record.
  const complianceRate = livestockBookings.length > 0 ? Math.round((fitForTransportCount / livestockBookings.length) * 100) : 100;

  const activeSailings = sailings.filter(s => ["loading", "departed"].includes(s.status));
  const upcomingSailings = sailings.filter(s => ["open", "draft"].includes(s.status));

  // Sum every livestockItem on a booking (not just the first) — used both for
  // per-booking head totals and as a fallback for a sailing's loaded count.
  const getBookingHeadTotal = (b) => (b.livestockItems || []).reduce((s, i) => s + Number(i.headCount || 0), 0);

  // "Loaded" is only ever incremented by NAIT tag scanning (handleScan below).
  // If scanning isn't in use, loadedHead sits at 0 forever even though the
  // vessel may have departed fully loaded. Once a sailing has actually
  // departed, fall back to the booked head count for that sailing instead of
  // showing a misleading 0.
  const getSailingLoadedHead = (s) => {
    const scanned = s.loading?.loadedHead || 0;
    if (scanned > 0) return scanned;
    if (s.status !== "departed") return 0;
    const sailingBookings = bookings.filter(b => String(b.sailing) === String(s._id) || String(b.sailing?._id) === String(s._id));
    return sailingBookings.reduce((sum, b) => sum + getBookingHeadTotal(b), 0);
  };

  const speciesBreakdown = useMemo(() => {
    const map = {};
    livestockBookings.forEach(b => {
      b.livestockItems?.forEach(item => {
        const type = item.livestockType || "cattle";
        map[type] = (map[type] || 0) + Number(item.headCount || 0);
      });
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [livestockBookings]);

  const totalHead = speciesBreakdown.reduce((s, [, v]) => s + v, 0);

  const handleScan = async (sailingId, eid) => {
    const sailing = sailings.find(s => s._id === sailingId);
    if (!sailing) return;

    // Client-side duplicate check for instant feedback before the network round-trip
    const existing = sailing.loading?.scannedTags || [];
    if (existing.some(t => t.eid === eid)) {
      setScanFeedback({ type: "duplicate", eid, message: "Already scanned for this sailing" });
      return;
    }

    const target = scanTargets[sailingId];
    if (!target) {
      setScanFeedback({ type: "unmatched", eid, message: "Select a pen / consignment before scanning" });
      return;
    }

    try {
      const res = await fetch(`${SAILINGS_API}/${sailingId}/scan-tag`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          eid,
          penNumber: target.penNumber,
          bookingId: target.bookingId,
          customerName: target.customerName,
        }),
      });

      if (res.status === 409) {
        setScanFeedback({ type: "duplicate", eid, message: "Already scanned for this sailing" });
        return;
      }
      if (!res.ok) throw new Error("Scan failed to save");

      const updatedSailing = await res.json();
      setSailings(prev => prev.map(s => s._id === sailingId ? updatedSailing : s));
      setScanFeedback({ type: "ok", eid, message: `Pen ${target.penNumber} — ${target.customerName}`, penNumber: target.penNumber });
    } catch (err) {
      console.error("Scan error:", err);
      setScanFeedback({ type: "unmatched", eid, message: "Failed to save scan — check connection" });
    }
  };

  return (
    <div className="w-full p-6 space-y-6">

        {/* Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Livestock</h1>
            <p className="text-sm text-gray-500 mt-1">Animal welfare, voyage monitoring and MPI compliance.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/dashboard/bookings/new" className="rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-800">
              + New Livestock Booking
            </Link>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1.5 border-b border-slate-200 pb-4">
          {TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${activeTab === tab.key ? "bg-slate-950 text-white shadow-sm" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── OVERVIEW ── */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
              <KpiCard label="Head This Month" value={headThisMonth.toLocaleString()} unit="head" />
              <KpiCard label="Head YTD" value={headYTD.toLocaleString()} unit="head" />
              <KpiCard label="On Voyage Now" value={activeSailings.reduce((s, v) => s + getSailingLoadedHead(v), 0)} unit="head" highlight={activeSailings.length > 0} />
              <KpiCard label="Livestock Bookings" value={livestockBookings.length} unit="total" />
              <KpiCard label="Fit for Transport Rate" value={`${complianceRate}%`} highlight={complianceRate < 100} />
              <KpiCard label="Incidents" value={incidents.length} highlight={incidents.length > 0} />
            </div>

            <div className="grid gap-6 xl:grid-cols-[1fr_360px]">
              {/* Recent bookings */}
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                  <h2 className="text-base font-semibold text-slate-900">Recent Livestock Bookings</h2>
                  <Link to="/dashboard/bookings" className="text-xs font-semibold text-slate-500 hover:text-slate-900">View all →</Link>
                </div>
                {loading ? (
                  <div className="px-5 py-10 text-sm text-slate-400 text-center">Loading...</div>
                ) : livestockBookings.length === 0 ? (
                  <div className="px-5 py-10 text-sm text-slate-400 text-center">No livestock bookings yet.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr className="text-left">
                        <th className="px-4 py-3 font-semibold text-gray-700">Ref</th>
                        <th className="px-4 py-3 font-semibold text-gray-700">Customer</th>
                        <th className="px-4 py-3 font-semibold text-gray-700">Species</th>
                        <th className="px-4 py-3 font-semibold text-gray-700">Class</th>
                        <th className="px-4 py-3 font-semibold text-gray-700">Head</th>
                        <th className="px-4 py-3 font-semibold text-gray-700">Status</th>
                        <th className="px-4 py-3 font-semibold text-gray-700 text-right">View</th>
                      </tr>
                    </thead>
                    <tbody>
                      {livestockBookings.slice(0, 10).map((b, i) => {
                        const ref = b.bookingRef || b._id?.slice(-6).toUpperCase();
                        // A booking can carry more than one livestock line (e.g. two
                        // classes) — use the first item for display but total the
                        // head count across all of them, not just item[0].
                        const items = b.livestockItems || [];
                        const lv = items[0] || {};
                        const headTotal = getBookingHeadTotal(b);
                        return (
                          <tr key={b._id} className="border-t border-gray-200 hover:bg-gray-50 transition">
                            <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">{ref}</td>
                            <td className="px-4 py-3 text-gray-700">{b.customerName || "—"}</td>
                            <td className="px-4 py-3 text-gray-500 capitalize">{lv.livestockType || "—"}{items.length > 1 ? ` +${items.length - 1}` : ""}</td>
                            <td className="px-4 py-3 text-gray-500 capitalize">{lv.animalClass || "—"}</td>
                            <td className="px-4 py-3 font-semibold text-gray-900">{headTotal}</td>
                            <td className="px-4 py-3"><StatusBadge status={b.status} /></td>
                            <td className="px-4 py-3 text-right">
                              <Link to={`/dashboard/bookings/${b._id}`} className="text-xs font-semibold text-gray-900 hover:underline">View</Link>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="space-y-4">
                {/* Active voyages */}
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                  <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-slate-900">Active Voyages</h2>
                    {activeSailings.length > 0 && <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">{activeSailings.length} live</span>}
                  </div>
                  {activeSailings.length === 0 ? (
                    <div className="px-5 py-6 text-sm text-slate-400 text-center">No active voyages.</div>
                  ) : activeSailings.map((s, i) => (
                    <div key={s._id} className={`px-5 py-4 ${i > 0 ? "border-t border-slate-100" : ""}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-semibold text-sm text-slate-900">{s.sailingRef}</div>
                        <StatusBadge status={s.status} />
                      </div>
                      <div className="text-xs text-slate-500 mb-3">{s.origin} → {s.destination}</div>
                      <div className="grid grid-cols-2 gap-2 mb-3">
                        <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2 text-center">
                          <div className="text-xs text-slate-400 mb-0.5">Expected</div>
                          <div className="font-bold text-slate-900">{s.loading?.expectedHead ?? "—"}</div>
                        </div>
                        <div className="rounded-xl bg-emerald-50 border border-emerald-100 px-3 py-2 text-center">
                          <div className="text-xs text-emerald-600 mb-0.5">Loaded</div>
                          <div className="font-bold text-emerald-700">{getSailingLoadedHead(s)}</div>
                        </div>
                      </div>
                      <Link to={`/dashboard/sailings/${s._id}`} className="block text-center rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition">
                        Open Sailing →
                      </Link>
                    </div>
                  ))}
                </div>

                {/* Species breakdown */}
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                  <div className="px-5 py-4 border-b border-slate-100">
                    <h2 className="text-sm font-semibold text-slate-900">Species Breakdown (YTD)</h2>
                  </div>
                  <div className="px-5 py-4 space-y-3">
                    {speciesBreakdown.length === 0 ? (
                      <div className="text-xs text-slate-400 text-center py-4">No data</div>
                    ) : speciesBreakdown.map(([species, count]) => {
                      const pct = totalHead > 0 ? Math.round((count / totalHead) * 100) : 0;
                      return (
                        <div key={species}>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="font-medium text-slate-700 capitalize">{species}</span>
                            <span className="text-slate-400">{count.toLocaleString()} head · {pct}%</span>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-2 bg-slate-800 rounded-full transition-all" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── LOADING TAB ── */}
        {activeTab === "loading" && (
          <div className="space-y-6">
            {activeSailings.length === 0 && (
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-8 text-center">
                <div className="text-sm font-semibold text-slate-600 mb-1">No active livestock voyages</div>
                <div className="text-xs text-slate-400 mb-4">Loading activity appears here when a sailing is in Loading or Departed status.</div>
                {upcomingSailings.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Upcoming Sailings</div>
                    {upcomingSailings.slice(0, 3).map(s => (
                      <Link key={s._id} to={`/dashboard/sailings/${s._id}`}
                        className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 hover:bg-slate-100 transition">
                        <div>
                          <div className="text-sm font-semibold text-slate-900">{s.sailingRef}</div>
                          <div className="text-xs text-slate-500">{s.origin} → {s.destination}</div>
                        </div>
                        <StatusBadge status={s.status} />
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeSailings.map(s => {
              const pens = s.loadPlan?.pens || [];
              const portPens = pens.filter(p => p.penNumber?.startsWith("P"));
              const starPens = pens.filter(p => p.penNumber?.startsWith("S"));
              const loaded = getSailingLoadedHead(s);
              const expected = s.loading?.expectedHead || 0;
              const pct = expected > 0 ? Math.round((loaded / expected) * 100) : 0;

              return (
                <div key={s._id} className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                  {/* Sailing header */}
                  <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-bold text-slate-900">{s.sailingRef}</span>
                        <StatusBadge status={s.status} />
                        {s.loadPlan?.status === "approved" && (
                          <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">Load Plan Approved</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500">{s.origin} → {s.destination} · {s.vesselName || "William Fraser"}</div>
                    </div>
                    <Link to={`/dashboard/sailings/${s._id}`}
                      className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                      Open Sailing →
                    </Link>
                  </div>

                  <div className="p-5 space-y-5">
                    {/* Loading progress */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Loading Progress</span>
                        <span className="text-xs font-bold text-slate-900">{loaded} / {expected} head · {pct}%</span>
                      </div>
                      <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-3 rounded-full transition-all ${pct >= 100 ? "bg-emerald-500" : pct >= 80 ? "bg-amber-400" : "bg-slate-800"}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                      </div>
                    </div>

                    {/* Scan Animals */}
                    {scanningEnabled ? (
                      <ScanAnimalsPanel
                        sailing={s}
                        pens={pens}
                        scannedTags={s.loading?.scannedTags || []}
                        scanTarget={scanTargets[s._id] || null}
                        onSetTarget={(target) => setScanTargets(prev => ({ ...prev, [s._id]: target }))}
                        onScan={(eid) => handleScan(s._id, eid)}
                        feedback={scanFeedback}
                        clearFeedback={() => setScanFeedback(null)}
                      />
                    ) : (
                      <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-500">
                        NAIT tag scanning is currently disabled.{" "}
                        <Link to="/dashboard/settings" className="font-semibold text-slate-700 underline">
                          Enable it in Settings → Booking
                        </Link>{" "}
                        when you're ready to start using handheld scanners.
                      </div>
                    )}

                    {/* Pen grid */}
                    {pens.length > 0 && (
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">Pen Allocation</div>
                        {portPens.length > 0 && (
                          <div className="mb-3">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-300 mb-2">Port</div>
                            <div className="flex flex-wrap gap-1.5">
                              {portPens.map((pen, i) => {
                                const alloc = pen.allocations?.reduce((s, a) => s + a.headCount, 0) || 0;
                                const penPct = pen.maxHead > 0 ? Math.round((alloc / pen.maxHead) * 100) : 0;
                                return (
                                  <div key={i} className={`rounded-lg border px-2.5 py-1.5 text-center min-w-[46px] ${pen.isHospitalPen ? "border-red-200 bg-red-50" : alloc > 0 ? penPct >= 90 ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}>
                                    <div className="text-[9px] font-bold text-slate-400">{pen.penNumber}</div>
                                    {pen.isHospitalPen
                                      ? <div className="text-[9px] text-red-500 font-bold">HOSP</div>
                                      : <div className={`text-xs font-bold ${alloc > 0 ? "text-slate-900" : "text-slate-300"}`}>{alloc > 0 ? `${alloc}/${pen.maxHead}` : "—"}</div>
                                    }
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {starPens.length > 0 && (
                          <div>
                            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-300 mb-2">Starboard</div>
                            <div className="flex flex-wrap gap-1.5">
                              {starPens.map((pen, i) => {
                                const alloc = pen.allocations?.reduce((s, a) => s + a.headCount, 0) || 0;
                                const penPct = pen.maxHead > 0 ? Math.round((alloc / pen.maxHead) * 100) : 0;
                                return (
                                  <div key={i} className={`rounded-lg border px-2.5 py-1.5 text-center min-w-[46px] ${pen.isHospitalPen ? "border-red-200 bg-red-50" : alloc > 0 ? penPct >= 90 ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}>
                                    <div className="text-[9px] font-bold text-slate-400">{pen.penNumber}</div>
                                    {pen.isHospitalPen
                                      ? <div className="text-[9px] text-red-500 font-bold">HOSP</div>
                                      : <div className={`text-xs font-bold ${alloc > 0 ? "text-slate-900" : "text-slate-300"}`}>{alloc > 0 ? `${alloc}/${pen.maxHead}` : "—"}</div>
                                    }
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Compliance checklist summary */}
                    {s.complianceChecklist && (
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Pre-Loading Checklist</div>
                        <div className="grid grid-cols-2 gap-1.5 md:grid-cols-3">
                          {[
                            { k: "farmerDeclarationReceived", l: "Farmer Declaration" },
                            { k: "loadPlanApproved", l: "Load Plan Approved" },
                            { k: "hospitalPenReserved", l: "Hospital Pen Reserved" },
                            { k: "prestartMeetingCompleted", l: "Prestart Meeting" },
                          ].map(item => (
                            <div key={item.k} className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium ${s.complianceChecklist[item.k] ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                              <span>{s.complianceChecklist[item.k] ? "✓" : "○"}</span>
                              {item.l}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── COMPLIANCE TAB ── */}
        {activeTab === "compliance" && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
              <KpiCard label="Fit for Transport" value={`${fitForTransportCount}/${livestockBookings.length}`} />
              <KpiCard label="Fit for Transport Rate" value={`${complianceRate}%`} highlight={complianceRate < 100} />
              <KpiCard label="Sailings with Approved Plan" value={sailings.filter(s => s.loadPlan?.status === "approved").length} />
            </div>

            {/* Per-voyage compliance */}
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100">
                <h2 className="text-base font-semibold text-slate-900">Voyage Compliance Status</h2>
              </div>
              {sailings.filter(s => s.status !== "cancelled").length === 0 ? (
                <div className="px-5 py-10 text-sm text-slate-400 text-center">No sailings.</div>
              ) : (
                <>
                  <div className="grid grid-cols-[100px_1fr_100px_120px_120px_120px_120px] gap-2 border-b border-slate-100 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
                    <div>Ref</div><div>Route</div><div>Status</div><div>Farmer Decl.</div><div>Load Plan</div><div>Hospital Pen</div><div>Prestart</div>
                  </div>
                  {sailings.filter(s => s.status !== "cancelled").slice(0, 20).map((s, i) => {
                    const cl = s.complianceChecklist || {};
                    return (
                      <div key={s._id} className={`grid grid-cols-[100px_1fr_100px_120px_120px_120px_120px] gap-2 px-5 py-3 text-sm items-center hover:bg-slate-50 transition ${i > 0 ? "border-t border-slate-100" : ""}`}>
                        <Link to={`/dashboard/sailings/${s._id}`} className="font-semibold text-slate-900 text-xs hover:underline">{s.sailingRef}</Link>
                        <div className="text-xs text-slate-500 truncate">{s.origin} → {s.destination}</div>
                        <StatusBadge status={s.status} />
                        <CompliancePill ok={cl.farmerDeclarationReceived} okLabel="✓" failLabel="✗ Missing" />
                        <CompliancePill ok={cl.loadPlanApproved || s.loadPlan?.status === "approved"} okLabel="✓ Approved" failLabel="Pending" />
                        <CompliancePill ok={cl.hospitalPenReserved} okLabel="✓" failLabel="✗ Missing" />
                        <CompliancePill ok={cl.prestartMeetingCompleted} okLabel="✓" failLabel="Pending" />
                      </div>
                    );
                  })}
                </>
              )}
            </div>

            {/* Declaration status by booking */}
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100">
                <h2 className="text-base font-semibold text-slate-900">Declaration Status by Booking</h2>
              </div>
              <div className="grid grid-cols-[auto_1fr_80px_140px_50px] gap-3 border-b border-slate-100 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
                <div>Ref</div><div>Customer</div><div>Head</div><div>Fit for Transport</div><div></div>
              </div>
              {livestockBookings.slice(0, 20).map((b, i) => {
                const ref = b.bookingRef || b._id?.slice(-6).toUpperCase();
                const shortRef = ref.length > 12 ? `…${ref.slice(-8)}` : ref;
                const items = b.livestockItems || [];
                const headTotal = getBookingHeadTotal(b);
                // Only "Declared" once every item on the booking is fit — matches
                // the fitForTransportCount logic above (was checking item[0] only).
                const allFit = items.length > 0 && items.every(i => i.fitForTransport);
                return (
                  <div key={b._id} className={`grid grid-cols-[auto_1fr_80px_140px_50px] gap-3 px-5 py-3.5 text-sm items-center hover:bg-slate-50 transition ${i > 0 ? "border-t border-slate-100" : ""}`}>
                    <div className="font-medium text-slate-900 text-xs whitespace-nowrap">{ref}</div>
                    <div className="text-slate-700 truncate">{b.customerName || "—"}</div>
                    <div className="text-xs font-semibold text-slate-900">{headTotal}</div>
                    <CompliancePill ok={allFit} okLabel="Declared" failLabel="Missing" />
                    <Link to={`/dashboard/bookings/${b._id}`} className="text-xs font-semibold text-slate-900 hover:underline">View</Link>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── INSPECTIONS TAB ── */}
        {activeTab === "inspections" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-slate-900">Daily Inspection Log</h2>
                <p className="text-xs text-slate-400 mt-0.5"></p>
              </div>
              <button
                onClick={() => setShowInspectionForm(true)}
                className="rounded-xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
              >
                + Log Inspection
              </button>
            </div>

            {/* Inspection form modal */}
            {showInspectionForm && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                <div className="w-full max-w-2xl rounded-3xl bg-white shadow-xl overflow-hidden max-h-[90vh] overflow-y-auto">
                  <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
                    <h3 className="font-bold text-slate-900">Log Inspection</h3>
                    <button onClick={() => setShowInspectionForm(false)} className="text-slate-400 hover:text-slate-700 text-2xl">×</button>
                  </div>
                  <div className="px-6 py-5 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <Field label="Sailing Ref">
                        <select className={INPUT_SM} value={inspection.sailingRef} onChange={e => setInspection(p => ({ ...p, sailingRef: e.target.value }))}>
                          <option value="">— Select —</option>
                          {activeSailings.map(s => <option key={s._id} value={s.sailingRef}>{s.sailingRef} — {s.origin} → {s.destination}</option>)}
                        </select>
                      </Field>
                      <Field label="Pen Number">
                        <input className={INPUT_SM} value={inspection.penNumber} onChange={e => setInspection(p => ({ ...p, penNumber: e.target.value }))} placeholder="e.g. P1, All" />
                      </Field>
                      <Field label="Date"><input type="date" className={INPUT_SM} value={inspection.date} onChange={e => setInspection(p => ({ ...p, date: e.target.value }))} /></Field>
                      <Field label="Time"><input type="time" className={INPUT_SM} value={inspection.time} onChange={e => setInspection(p => ({ ...p, time: e.target.value }))} /></Field>
                    </div>

                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mt-2">Animal Health</div>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { k: "animalsStanding", l: "Animals standing & alert?" },
                        { k: "lameness", l: "Lameness or injury noted?" },
                        { k: "heatStress", l: "Signs of heat stress?" },
                      ].map(item => (
                        <Field key={item.k} label={item.l}>
                          <select className={INPUT_SM} value={inspection[item.k]} onChange={e => setInspection(p => ({ ...p, [item.k]: e.target.value }))}>
                            <option value="">—</option>
                            <option value="ok">OK</option>
                            <option value="action">Action Required</option>
                          </select>
                        </Field>
                      ))}
                    </div>

                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Feed & Water</div>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { k: "waterOk", l: "Water troughs clean & full?" },
                        { k: "fodderOk", l: "Fodder available per ration plan?" },
                      ].map(item => (
                        <Field key={item.k} label={item.l}>
                          <select className={INPUT_SM} value={inspection[item.k]} onChange={e => setInspection(p => ({ ...p, [item.k]: e.target.value }))}>
                            <option value="">—</option>
                            <option value="ok">OK</option>
                            <option value="action">Action Required</option>
                          </select>
                        </Field>
                      ))}
                    </div>

                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Environment</div>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { k: "deckDry", l: "Deck dry & non-slip?" },
                        { k: "ventilation", l: "Sufficient ventilation?" },
                        { k: "gatesOk", l: "Gates & rails secure?" },
                      ].map(item => (
                        <Field key={item.k} label={item.l}>
                          <select className={INPUT_SM} value={inspection[item.k]} onChange={e => setInspection(p => ({ ...p, [item.k]: e.target.value }))}>
                            <option value="">—</option>
                            <option value="ok">OK</option>
                            <option value="action">Action Required</option>
                          </select>
                        </Field>
                      ))}
                    </div>

                    <Field label="Ammonia Level (ppm)">
                      <input type="number" className={INPUT_SM} value={inspection.ammonia} onChange={e => setInspection(p => ({ ...p, ammonia: e.target.value }))} placeholder="0" />
                    </Field>

                    <Field label="Notes / Actions Taken">
                      <textarea className={`${INPUT_SM} resize-none`} rows={3} value={inspection.notes} onChange={e => setInspection(p => ({ ...p, notes: e.target.value }))} placeholder="Observations, actions taken..." />
                    </Field>

                    <div className="flex justify-end gap-3 pt-2">
                      <button onClick={() => setShowInspectionForm(false)} className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
                      <button
                        onClick={async () => {
                          try {
                            const res = await fetch(INSPECTIONS_API, {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify(inspection),
                            });
                            if (!res.ok) throw new Error("Failed to save inspection");
                            const saved = await res.json();
                            setInspections(p => [saved, ...p]);
                            setShowInspectionForm(false);
                            setInspection({ sailingRef: "", penNumber: "", date: new Date().toISOString().slice(0, 10), time: "", animalsStanding: "", lameness: "", heatStress: "", waterOk: "", fodderOk: "", deckDry: "", ventilation: "", ammonia: "", gatesOk: "", notes: "" });
                          } catch (err) {
                            alert("Could not save inspection: " + err.message);
                          }
                        }}
                        className="rounded-xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                      >
                        Save Inspection
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {inspections.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-12 text-center">
                <div className="text-sm font-semibold text-slate-500 mb-1">No inspections logged</div>
                <div className="text-xs text-slate-400">Hourly inspection records will appear here.</div>
              </div>
            ) : (
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="grid grid-cols-[140px_110px_90px_110px_1fr_100px] gap-4 border-b border-slate-100 px-6 py-3.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  <div>
                    <div>Sailing</div>
                    <div className="normal-case font-normal text-[10px] text-slate-300 mt-0.5">Route</div>
                  </div>
                  <div>Date</div><div>Time</div><div>Ammonia</div><div>Status</div><div>Pen</div>
                </div>
                {inspections.map((ins, i) => {
                  const issues = [ins.animalsStanding === "action", ins.lameness === "action", ins.heatStress === "action", ins.waterOk === "action", ins.fodderOk === "action"].filter(Boolean).length;
                  const rowId = ins._id || i;
                  const isExpanded = expandedInspectionIds.has(rowId);
                  const toggleExpanded = () => {
                    setExpandedInspectionIds((prev) => {
                      const next = new Set(prev);
                      if (next.has(rowId)) next.delete(rowId);
                      else next.add(rowId);
                      return next;
                    });
                  };
                  const formattedDate = ins.date
                    ? new Date(ins.date).toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" })
                    : "—";
                  const matchedSailing = sailings.find((s) => s.sailingRef === ins.sailingRef);
                  return (
                    <div key={rowId} className={`grid grid-cols-[140px_110px_90px_110px_1fr_100px] gap-4 px-6 py-4 text-sm items-start ${i > 0 ? "border-t border-slate-100" : ""} hover:bg-slate-50/60 transition-colors`}>
                      <div>
                        <div className="font-semibold text-sm text-slate-900">{ins.sailingRef || "—"}</div>
                        {matchedSailing && (
                          <div className="text-xs text-slate-400 mt-0.5">{matchedSailing.origin} → {matchedSailing.destination}</div>
                        )}
                      </div>
                      <div className="text-sm text-slate-500">{formattedDate}</div>
                      <div className="text-sm text-slate-500">{ins.time || "—"}</div>
                      <div className={`text-sm font-semibold ${Number(ins.ammonia) >= 25 ? "text-red-600" : "text-slate-700"}`}>{ins.ammonia ? `${ins.ammonia} ppm` : "—"}</div>
                      <div>
                        <button
                          type="button"
                          onClick={toggleExpanded}
                          disabled={!ins.notes}
                          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${issues > 0 ? "text-amber-600 bg-amber-50 hover:bg-amber-100" : "text-emerald-600 bg-emerald-50 hover:bg-emerald-100"} ${!ins.notes ? "cursor-default" : "cursor-pointer"}`}
                        >
                          {issues > 0 ? `${issues} action${issues > 1 ? "s" : ""} required` : "All OK"}
                          {ins.notes && (
                            <svg className={`h-3 w-3 transition-transform ${isExpanded ? "rotate-180" : ""}`} viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>
                        {ins.notes && isExpanded && (
                          <div className="mt-2 text-xs text-slate-400 leading-relaxed max-w-2xl">{ins.notes}</div>
                        )}
                      </div>
                      <div className="text-sm text-slate-500 capitalize">{ins.penNumber || "All"}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── INCIDENTS TAB ── */}
        {activeTab === "incidents" && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <KpiCard label="Total Incidents" value={incidents.length} highlight={incidents.length > 0} />
              <KpiCard label="Mortalities" value={incidents.filter(i => i.type === "Mortality").length} highlight={incidents.filter(i => i.type === "Mortality").length > 0} />
              <KpiCard label="Injuries" value={incidents.filter(i => i.type === "Injury").length} />
              <KpiCard label="Days Since Last" value={incidents.length > 0 ? Math.floor((Date.now() - new Date(incidents[0].date)) / 86400000) : "—"} />
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-base font-semibold text-slate-900">Incident Register</h2>
                <button onClick={() => setShowIncidentForm(true)} className="rounded-xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800">
                  + Log Incident
                </button>
              </div>

              {/* Incident form modal */}
              {showIncidentForm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                  <div className="w-full max-w-lg rounded-3xl bg-white shadow-xl overflow-hidden">
                    <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
                      <h3 className="font-bold text-slate-900">Log Welfare Incident</h3>
                      <button onClick={() => setShowIncidentForm(false)} className="text-slate-400 hover:text-slate-700 text-2xl">×</button>
                    </div>
                    <div className="px-6 py-5 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <Field label="Date"><input type="date" className={INPUT_SM} value={incident.date} onChange={e => setIncident(p => ({ ...p, date: e.target.value }))} /></Field>
                        <Field label="Incident Type">
                          <select className={INPUT_SM} value={incident.type} onChange={e => setIncident(p => ({ ...p, type: e.target.value }))}>
                            <option value="">— Select type —</option>
                            {INCIDENT_TYPES.map(t => <option key={t}>{t}</option>)}
                          </select>
                        </Field>
                        <Field label="Sailing Ref">
                          <select className={INPUT_SM} value={incident.sailingRef} onChange={e => setIncident(p => ({ ...p, sailingRef: e.target.value }))}>
                            <option value="">— Select sailing —</option>
                            {sailings.map(s => <option key={s._id} value={s.sailingRef}>{s.sailingRef}</option>)}
                          </select>
                        </Field>
                        <Field label="Pen Number">
                          <input className={INPUT_SM} value={incident.penNumber} onChange={e => setIncident(p => ({ ...p, penNumber: e.target.value }))} placeholder="e.g. P3" />
                        </Field>
                        <Field label="Animal Tag ID (if known)">
                          <input className={INPUT_SM} value={incident.tagId} onChange={e => setIncident(p => ({ ...p, tagId: e.target.value }))} placeholder="e.g. NZ123456" />
                        </Field>
                      </div>
                      <Field label="Description">
                        <textarea className={`${INPUT_SM} resize-none`} rows={3} value={incident.description} onChange={e => setIncident(p => ({ ...p, description: e.target.value }))} placeholder="Describe what happened..." />
                      </Field>
                      <Field label="Outcome / Action Taken">
                        <textarea className={`${INPUT_SM} resize-none`} rows={2} value={incident.outcome} onChange={e => setIncident(p => ({ ...p, outcome: e.target.value }))} placeholder="Treatment administered, animal segregated, euthanised..." />
                      </Field>
                      <div className="flex justify-end gap-3 pt-2">
                        <button onClick={() => setShowIncidentForm(false)} className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
                        <button
                          onClick={async () => {
                            if (!incident.type || !incident.description) { alert("Please fill in type and description."); return; }
                            try {
                              const res = await fetch(INCIDENTS_API, {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify(incident),
                              });
                              if (!res.ok) throw new Error("Failed to save incident");
                              const saved = await res.json();
                              setIncidents(p => [saved, ...p]);
                              setShowIncidentForm(false);
                              setIncident({ type: "", sailingRef: "", penNumber: "", tagId: "", description: "", outcome: "", date: new Date().toISOString().slice(0, 10) });
                            } catch (err) {
                              alert("Could not save incident: " + err.message);
                            }
                          }}
                          className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
                        >
                          Log Incident
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {incidents.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center">
                  <div className="text-sm font-semibold text-slate-500 mb-1">No incidents recorded</div>
                  <div className="text-xs text-slate-400">Welfare incidents, mortalities and near-misses will appear here.</div>
                </div>
              ) : (
                <div className="space-y-3">
                  {incidents.map((inc, i) => (
                    <div key={inc._id || i} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${inc.type === "Mortality" ? "bg-red-100 text-red-700" : inc.type === "Injury" ? "bg-orange-100 text-orange-700" : "bg-amber-100 text-amber-700"}`}>{inc.type}</span>
                          {inc.sailingRef && <span className="text-xs text-slate-500">{inc.sailingRef}</span>}
                          {inc.penNumber && <span className="text-xs text-slate-400">Pen {inc.penNumber}</span>}
                          {inc.tagId && <span className="text-xs text-slate-400">Tag: {inc.tagId}</span>}
                        </div>
                        <span className="text-xs text-slate-400">{inc.date}</span>
                      </div>
                      <p className="text-sm text-slate-700">{inc.description}</p>
                      {inc.outcome && <p className="text-xs text-slate-500 mt-1 italic">Action: {inc.outcome}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── REPORTS TAB ── */}
        {activeTab === "reports" && (
          <div className="space-y-5">
            {/* Voyage selector */}
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
              <div className="flex flex-col gap-3 md:flex-row md:items-end">
                <div className="flex-1">
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Select Sailing</label>
                  <select
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-950"
                    value={reportSailingId}
                    onChange={e => setReportSailingId(e.target.value)}
                  >
                    <option value="">— All sailings —</option>
                    {sailings.map(s => (
                      <option key={s._id} value={s._id}>
                        {s.sailingRef} — {s.origin} → {s.destination} ({s.status})
                      </option>
                    ))}
                  </select>
                </div>
                <p className="text-xs text-slate-400 pb-2.5">Select a voyage then click Generate on any report below.</p>
              </div>
            </div>

            {/* Report cards */}
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {[
                { title: "Load Report", desc: "Pen allocation, head counts and load plan for a voyage. Includes MPI density compliance.", icon: "clipboard", format: ["Pen allocation table", "Species and class", "Fit for transport status", "MPI density check"] },
                { title: "Welfare & Inspection Report", desc: "Hourly inspection records and compliance checklist for MNZ Rule Part 24c.", icon: "eye", format: ["Inspection log", "Feed & water records", "Ammonia readings", "Compliance checklist"] },
                { title: "Animal Movement Report", desc: "NAIT-ready animal movement record. Retain for 7 years under NAIT Act 2012.", icon: "map", format: ["Owner and consignor details", "Species, class and head count", "Origin and destination", "Movement date"] },
                { title: "Livestock Manifest", desc: "Printable manifest for MPI submission. Covers all livestock on a voyage.", icon: "file", format: ["Full consignment list", "Weights and head count", "Declaration status", "Pen assignments"] },
                { title: "Incident Report", desc: "Welfare incident register with outcomes and sign-off block.", icon: "alert", format: ["Incident timeline", "Affected animals and pens", "Actions taken", "Stockman sign-off"] },
                { title: "MPI Audit Report", desc: "Full compliance summary for MPI veterinary officer or audit review.", icon: "check", format: ["All compliance checks", "MNZ Rule Part 24c Table 4", "Incident summary", "Certification status"] },
              ].map(report => (
                <div key={report.title} className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 flex flex-col gap-4">
                  <div>
                    <div className="font-semibold text-slate-900 mb-1">{report.title}</div>
                    <div className="text-xs text-slate-500 leading-relaxed">{report.desc}</div>
                  </div>
                  <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Contains</div>
                    <div className="space-y-1">
                      {report.format.map(section => (
                        <div key={section} className="flex items-center gap-2 text-xs text-slate-600">
                          <span className="text-emerald-500">✓</span>
                          <span>{section}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <button
                    className="mt-auto w-full rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition"
                    disabled={!reportSailingId}
                    onClick={() => generateReport(report.title, reportSailingId)}
                  >
                    {reportSailingId ? "Generate & Print" : "Select a voyage first"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

    </div>
  );
}

// ── HELPER COMPONENTS ──

function ScanAnimalsPanel({ sailing, pens, scannedTags, scanTarget, onSetTarget, onScan, feedback, clearFeedback }) {
  const [inputValue, setInputValue] = useState("");
  const [active, setActive] = useState(false);
  const inputRef = useRef(null);

  // Build a flat list of pen/consignment options for the target picker.
  const targets = useMemo(() => {
    const list = [];
    pens.forEach(pen => {
      (pen.allocations || []).forEach(a => {
        list.push({
          penNumber: pen.penNumber,
          bookingId: a.bookingId,
          customerName: a.customerName,
          animalClass: a.animalClass,
          headCount: a.headCount,
        });
      });
    });
    return list;
  }, [pens]);

  // Auto-clear feedback after a few seconds
  useEffect(() => {
    if (!feedback) return;
    const t = setTimeout(() => clearFeedback(), 3500);
    return () => clearTimeout(t);
  }, [feedback]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      const eid = inputValue.trim();
      if (eid.length > 0) {
        onScan(eid);
        setInputValue("");
      }
    }
  };

  const scannedCountForTarget = (target) => {
    if (!target) return 0;
    return scannedTags.filter(t => t.bookingId === target.bookingId && t.penNumber === target.penNumber).length;
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Scan Animals (NAIT RFID)</span>
        <span className="text-xs text-slate-400">{scannedTags.length} scanned this voyage</span>
      </div>

      {/* Step 1: pick pen / consignment */}
      <div className="mb-3">
        <label className="block text-xs text-slate-500 mb-1.5">Loading into</label>
        <select
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-950"
          value={scanTarget ? `${scanTarget.penNumber}__${scanTarget.bookingId}` : ""}
          onChange={(e) => {
            const [penNumber, bookingId] = e.target.value.split("__");
            const match = targets.find(t => t.penNumber === penNumber && String(t.bookingId) === bookingId);
            onSetTarget(match || null);
          }}
        >
          <option value="">— Select pen / consignment —</option>
          {targets.map((t, i) => (
            <option key={i} value={`${t.penNumber}__${t.bookingId}`}>
              {t.penNumber} — {t.customerName} ({t.animalClass || "—"}, {t.headCount} hd)
            </option>
          ))}
        </select>
      </div>

      {/* Step 2: scan input */}
      <div className="mb-3">
        <label className="block text-xs text-slate-500 mb-1.5">
          Scan tag {scanTarget && <span className="text-slate-400">— {scannedCountForTarget(scanTarget)}/{scanTarget.headCount} loaded into {scanTarget.penNumber}</span>}
        </label>
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setActive(true)}
          onBlur={() => setActive(false)}
          disabled={!scanTarget}
          placeholder={scanTarget ? "Ready to scan — tap here then scan tag" : "Select a pen / consignment first"}
          className={`w-full rounded-lg border-2 px-4 py-3 text-base font-mono outline-none transition ${
            active ? "border-slate-950 bg-white" : "border-slate-200 bg-white"
          } disabled:bg-slate-100 disabled:text-slate-400`}
          autoComplete="off"
        />
      </div>

      {/* Feedback */}
      {feedback && (
        <div className={`rounded-lg px-4 py-2.5 text-sm font-medium mb-3 ${
          feedback.type === "ok" ? "bg-emerald-100 text-emerald-800" :
          feedback.type === "duplicate" ? "bg-amber-100 text-amber-800" :
          "bg-red-100 text-red-800"
        }`}>
          {feedback.type === "ok" && "✓ "}
          {feedback.type === "duplicate" && "⚠ Duplicate — "}
          {feedback.type === "unmatched" && "✗ "}
          <span className="font-mono">{feedback.eid}</span> — {feedback.message}
        </div>
      )}

      {/* Recent scans */}
      {scannedTags.length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Recent Scans</div>
          <div className="max-h-40 overflow-y-auto space-y-1">
            {[...scannedTags].reverse().slice(0, 10).map((tag, i) => (
              <div key={i} className="flex items-center justify-between text-xs bg-white rounded-lg px-3 py-2 border border-slate-100">
                <span className="font-mono text-slate-700">{tag.eid}</span>
                <span className="text-slate-400">{tag.penNumber} · {tag.customerName}</span>
                <span className="text-slate-300">{new Date(tag.scannedAt).toLocaleTimeString("en-NZ", { hour: "2-digit", minute: "2-digit" })}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="mt-3 text-xs text-slate-400">
        Tap the scan field, then scan each animal's NAIT tag with the handheld reader. Pen assignment stays manual — scanning only logs which animals were loaded.
      </p>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function KpiCard({ label, value, unit, highlight }) {
  return (
    <div className={`rounded-2xl border bg-white p-5 shadow-sm ${highlight ? "border-amber-200 bg-amber-50" : "border-slate-200"}`}>
      <div className={`text-xs font-semibold uppercase tracking-wide mb-2 ${highlight ? "text-amber-600" : "text-slate-400"}`}>{label}</div>
      <div className={`text-2xl font-bold tracking-tight ${highlight ? "text-amber-900" : "text-slate-950"}`}>{value}</div>
      {unit && <div className="text-xs text-slate-400 mt-0.5">{unit}</div>}
    </div>
  );
}

function StatusBadge({ status }) {
  const s = String(status || "pending").toLowerCase();
  const styles =
    s === "confirmed" ? "bg-emerald-50 text-emerald-700" :
    s === "loading" ? "bg-blue-50 text-blue-700" :
    s === "departed" ? "bg-purple-50 text-purple-700" :
    s === "arrived" ? "bg-teal-50 text-teal-700" :
    s === "cancelled" ? "bg-red-50 text-red-700" :
    "bg-amber-50 text-amber-700";
  return <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium capitalize ${styles}`}>{s}</span>;
}

function CompliancePill({ ok, okLabel, failLabel }) {
  return ok
    ? <span className="inline-flex rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">{okLabel}</span>
    : <span className="inline-flex rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-600">{failLabel}</span>;
}


function ReportIcon({ name, className = "h-5 w-5" }) {
  const common = {
    className,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
  };

  const icons = {
    clipboard: (
      <svg {...common}>
        <path d="M9 5h6" />
        <path d="M9 3h6a2 2 0 0 1 2 2v1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h1V5a2 2 0 0 1 2-2Z" />
        <path d="M8 12h8" />
        <path d="M8 16h6" />
      </svg>
    ),
    ship: (
      <svg {...common}>
        <path d="M3 17h18" />
        <path d="M5 17l2-8h10l2 8" />
        <path d="M9 9V5h6v4" />
        <path d="M7 21c1.2 0 1.8-.5 2.5-1s1.3-1 2.5-1 1.8.5 2.5 1 1.3 1 2.5 1" />
      </svg>
    ),
    location: (
      <svg {...common}>
        <path d="M12 21s7-4.7 7-11a7 7 0 1 0-14 0c0 6.3 7 11 7 11Z" />
        <circle cx="12" cy="10" r="2.5" />
      </svg>
    ),
    file: (
      <svg {...common}>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
        <path d="M14 2v6h6" />
        <path d="M8 13h8" />
        <path d="M8 17h6" />
      </svg>
    ),
    alert: (
      <svg {...common}>
        <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
        <path d="M12 9v4" />
        <path d="M12 17h.01" />
      </svg>
    ),
    check: (
      <svg {...common}>
        <path d="M20 6 9 17l-5-5" />
      </svg>
    ),
  };

  return icons[name] || icons.file;
}